import { toast } from 'react-toastify';

import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';

import { useDisconnect } from 'wagmi';

import { RequestSignResult } from '@api';

import { GetRewardPoolsData, joinRewardPool } from '@shared/api/rewardPools';
import { REWARD_POOL_ACCESS_POLICY_OPERATOR, RewardPool } from '@shared/api/types/rewardPools';
import { normalizeError } from '@shared/helpers/displayErrorToast';
import { getErrorMessage } from '@shared/helpers/error';
import getWalletActionStyles from '@shared/helpers/web3/getWalletActionStyles';
import {
  ALL_REWARD_POOLS_INFINITE_QUERIES_KEY,
  getIsSelfJoinedRewardPoolQueryOptions,
  getSelfRewardPoolLeaderboardDataQueryOptions,
  rewardPoolsQueryKeys,
  useRewardPoolAccessPoliciesQuery,
} from '@shared/hooks/query/rewardPools';
import useMagicIdUserWalletInfo from '@shared/hooks/useMagicIdUserWalletInfo';
import useWeb3, { RequestSignProps } from '@shared/hooks/web3/useWeb3';

const useJoinRewardPool = (rewardPoolId: number) => {
  const { disconnectAsync } = useDisconnect();
  const { requestSign } = useWeb3();
  const { getUserWalletType } = useMagicIdUserWalletInfo();
  const { refetch } = useRewardPoolAccessPoliciesQuery(rewardPoolId, {
    enabled: false,
  });

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (rewardPool: RewardPool) => {
      const toastId = new Date().getTime();

      const handleSignResult = async (result?: RequestSignResult) => {
        if (!result) {
          return;
        }

        const { address, message, signature } = result;

        try {
          await joinRewardPool({
            rewardPoolId: rewardPool.id,
            data: {
              message,
              pubKey: address,
              signature,
            },
          });
        } catch (error) {
          const errorMessage = getErrorMessage(error);

          if (errorMessage.includes('access policies')) {
            throw new Error('Please complete all pool requirement before joining the pool.');
          }

          throw error;
        }
      };

      const { data: accessPolicies, error: accessPoliciesError } = await refetch();

      if (accessPoliciesError) {
        throw accessPoliciesError;
      }

      if (!accessPolicies) {
        throw new Error("We couldn't verify if the requirements were met. Please try again.");
      }

      if (rewardPool.access_policy.operator === REWARD_POOL_ACCESS_POLICY_OPERATOR.AND) {
        const isEveryRequirementMet = accessPolicies.every(({ qualified }) => qualified);

        if (!isEveryRequirementMet) {
          throw new Error('Please make sure all pool requirements are met.');
        }
      } else {
        const isSomeRequirementMet = accessPolicies.some(({ qualified }) => qualified);

        if (!isSomeRequirementMet) {
          throw new Error('Please ensure at least one of the pool requirement is met.');
        }
      }

      try {
        const { walletType, userWallet } = getUserWalletType();

        const message = `Join ${rewardPool.name} Reward Pool`;

        const signProps: RequestSignProps = {
          walletType,
          message: `${message} at Magic Store`,
          targetAddress: userWallet.attributes.address,
          signType: 'SIWE',
        };

        const style = getWalletActionStyles(walletType);
        toast.loading('Loading...', { toastId, style });
        const results = await requestSign(signProps);

        if (!results) {
          throw new Error("Couldn't join the pool.");
        }

        await handleSignResult(results);
      } catch (error) {
        const normalizedError = normalizeError(error);

        if (normalizedError.message === 'Account not provided') {
          await disconnectAsync();
        }

        throw normalizedError;
      } finally {
        toast.dismiss(toastId);
      }
    },
    onSuccess: (_, rewardPool) => {
      queryClient.setQueryData(getIsSelfJoinedRewardPoolQueryOptions().queryKey, (data) => {
        if (!data) {
          return {
            pools: [],
            myPoolIds: [rewardPool.id],
          };
        }

        return {
          ...data,
          myPoolIds: [...data.myPoolIds, rewardPool.id],
        };
      });

      queryClient.setQueriesData(
        {
          queryKey: [ALL_REWARD_POOLS_INFINITE_QUERIES_KEY],
        },
        (data?: InfiniteData<GetRewardPoolsData>) => {
          if (!data) {
            return data;
          }

          return {
            ...data,
            pages: data.pages.map((page) => {
              return {
                ...page,
                pools: page.pools.map((pool) => {
                  if (pool.id === rewardPool.id) {
                    return {
                      ...pool,
                      participants_counter: pool.participants_counter + 1,
                    };
                  }

                  return pool;
                }),
              };
            }),
          };
        },
      );

      queryClient.invalidateQueries({
        queryKey: rewardPoolsQueryKeys.rewardPool(rewardPool.id),
      });

      queryClient.invalidateQueries({
        queryKey: rewardPoolsQueryKeys.rewardPoolLeaderboardInfinite(rewardPool.id),
      });

      queryClient.invalidateQueries({
        queryKey: getSelfRewardPoolLeaderboardDataQueryOptions(rewardPool.id).queryKey,
      });
    },
  });
};

export default useJoinRewardPool;
